/*
 * Non-secure storage
 *
 * The last received service notice and the moment it was dismissed are persisted so
 * that the notice state is known immediately on the next app start, even without a
 * network connection.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

import { ServiceNotice } from './service-notice-client';

enum ServiceNoticeStorageKey {
    NOTICE = 'service-notice',
    DISMISSED_AT = 'service-notice-dismissed-at',
    ENDED_BY_FALLBACK = 'service-notice-ended-by-fallback'
}

/**
 * Returns the persisted service notice, or null when there is none.
 * A corrupt or unexpected stored value degrades to null (treated as "no notice"),
 * so bad storage content can never make the notice validity appear to have passed.
 */
export async function getPersistedNotice(): Promise<ServiceNotice | null> {
    const value = await AsyncStorage.getItem(ServiceNoticeStorageKey.NOTICE);
    if (!value) {
        return null;
    }
    try {
        const parsed = JSON.parse(value);
        // Only accept the value if it carries a validUntil that is a string AND parses
        // to a real date, since all notice validity decisions are based on it. Rejecting
        // a non-date validUntil here also stops the validity timer in service-notice.tsx
        // from computing a NaN delay and rescheduling in a tight loop.
        if (
            parsed &&
            typeof parsed === 'object' &&
            typeof parsed.validUntil === 'string' &&
            !Number.isNaN(Date.parse(parsed.validUntil))
        ) {
            return parsed as ServiceNotice;
        }
    } catch {
        // Ignore JSON parse errors and fall through to "no notice"
    }
    return null;
}

export const setPersistedNotice = (notice: ServiceNotice) =>
    AsyncStorage.setItem(ServiceNoticeStorageKey.NOTICE, JSON.stringify(notice));

export const clearPersistedNotice = () => AsyncStorage.removeItem(ServiceNoticeStorageKey.NOTICE);

/**
 * Returns the moment the member last dismissed the notice, or null when it has not
 * been dismissed (or the stored value is not a valid date).
 */
export async function getNoticeDismissedAt(): Promise<Date | null> {
    const value = await AsyncStorage.getItem(ServiceNoticeStorageKey.DISMISSED_AT);
    if (!value) {
        return null;
    }
    const dismissedAt = new Date(value);
    return isNaN(dismissedAt.getTime()) ? null : dismissedAt;
}

export const setNoticeDismissedAt = (dismissedAt: Date) =>
    AsyncStorage.setItem(ServiceNoticeStorageKey.DISMISSED_AT, dismissedAt.toISOString());

export const clearNoticeDismissedAt = () => AsyncStorage.removeItem(ServiceNoticeStorageKey.DISMISSED_AT);

/**
 * Returns true when the service was already found to be closed by the fallback end date
 * (see service-notice-fallback.ts), i.e. the server could not be reached after that moment.
 * Remembering it keeps the end-of-service screen up on the next start without another
 * network attempt.
 */
export async function getServiceEndedByFallback(): Promise<boolean> {
    return (await AsyncStorage.getItem(ServiceNoticeStorageKey.ENDED_BY_FALLBACK)) === 'true';
}

export const setServiceEndedByFallback = () => AsyncStorage.setItem(ServiceNoticeStorageKey.ENDED_BY_FALLBACK, 'true');

/**
 * Forgets an earlier fallback decision. Must be called every time the server answers, because
 * an answer proves the service is alive: without this, one failed request after the fallback
 * end date would keep the app closed for good even after the server started answering again.
 */
export const clearServiceEndedByFallback = () => AsyncStorage.removeItem(ServiceNoticeStorageKey.ENDED_BY_FALLBACK);
